import {forwardRef, useId} from 'react';
import cx from 'classnames';
import Image from 'next/image';

import {Button, Card, Icon, Text} from '#component/base';
import {ModalRoot, ModalTrigger} from '#component/base/Modal';
import {ActionItemModal} from '#component/modal';
import {EventType, useAnalytics, UserActionEvent} from '#component/provider/AnalyticsProvider';
import {sprinkles} from '#style/sprinkles';

import {QuestionCardProps} from './QuestionCard.types';

import * as css from './QuestionCard.css';

/**
 * This component renders an card for prompting the user with a question,
 * primarily used on the Check-in page.
 */
export const QuestionCard = forwardRef<HTMLDivElement, QuestionCardProps>(
  (
    {
      className,
      context,
      contextTitle,
      healthFactorId,
      id,
      imageUrl,
      impactLevel,
      onChoose,
      title,
      ...otherProps
    },
    ref,
  ) => {
    const {track} = useAnalytics();
    const downId = useId();
    const upId = useId();

    const handlePressYes = () => {
      track<UserActionEvent>({
        evtName: EventType.UserAction,
        actionName: 'Click yes',
        data: {healthFactorId},
      });
      onChoose?.({id, answerBoolean: true});
    };

    const handlePressNo = () => {
      track<UserActionEvent>({
        evtName: EventType.UserAction,
        actionName: 'Click no',
        data: {healthFactorId},
      });
      onChoose?.({id, answerBoolean: false});
    };

    const handlePressInfo = () => {
      track<UserActionEvent>({
        evtName: EventType.UserAction,
        actionName: 'View checkin tooltip',
        data: {healthFactorId},
      });
    };

    return (
      <Card
        className={cx(css.root({impactLevel: impactLevel || undefined}), className)}
        ref={ref}
        variant="ecruWhite"
        {...otherProps}>
        {context && (
          <ModalRoot>
            <ModalTrigger
              className={sprinkles({
                content: 'center',
                flex: 'flex',
                items: 'center',
                m: [0, 12],
                pos: 'absolute',
                right: 0,
                size: 56,
                top: 0,
              })}
              onClick={handlePressInfo}>
              <Icon aria-label="Learn more" color="eggplantDark" name="Info" />
            </ModalTrigger>

            <ActionItemModal headline={contextTitle} content={context} />
          </ModalRoot>
        )}

        <div
          className={sprinkles({content: 'center', dir: 'rowReverse', flex: 'flex', mT: [8, 24]})}>
          <div className={css.imageBg({impactLevel: impactLevel || undefined})} />

          <div className={css.image}>
            <Image
              alt=""
              aria-hidden
              className={sprinkles({pos: 'absolute', inset: 0})}
              layout="fill"
              src={imageUrl || ''}
            />
          </div>
        </div>

        <p className={sprinkles({dir: 'col', flex: 'flex', items: 'center', gap: 4})}>
          <Text color="burgundy" textAlign="center">
            Did you…
          </Text>

          <Text color="burgundy" fontWeight="semiBold" textAlign="center" variant="bodyXLarge">
            {title}
          </Text>
        </p>

        <div className={sprinkles({flex: 'flex', content: 'between', pX: [0, 44]})}>
          <div className={sprinkles({flex: 'flex', gap: [8, 16], items: 'center'})}>
            <Button
              aria-labelledby={downId}
              icon="ThumbsDown"
              onClick={handlePressNo}
              variant="secondary"
            />

            <Text color="eggplantDark" id={downId} variant="label">
              No
            </Text>
          </div>

          <div className={sprinkles({flex: 'flex', gap: [8, 16], items: 'center'})}>
            <Text color="eggplantDark" id={upId} variant="label">
              Yes
            </Text>

            <Button
              aria-labelledby={upId}
              icon="ThumbsUp"
              onClick={handlePressYes}
              variant="secondary"
            />
          </div>
        </div>
      </Card>
    );
  },
);
